import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  ScammerOverride as Override,
  ScammerOverrideService,
  AdminFusionAuthAccountAccessService,
} from 'us-web-services';
import DisplayService from '../../util/DisplayService';
import useAuthentication from '../../store/useAuthentication';
import DateUtil from '../../util/DateUtil';
import CardStyles from '../../styles/CardStyles';

interface Props extends RouteComponentProps {
  userId: number;
}

function ScammerOverride(props: Props) {
  const { userId } = props;
  const { classes } = CardStyles();

  const authenticationState = useAuthentication()[0];
  const [override, setOverride] = useState<Override>({});
  const [accountLocked, setAccountLocked] = useState<boolean | null>(null);
  const [accountChangedAt, setAccountChangedAt] = useState<number>();
  const [accountChangedBy, setAccountChangedBy] = useState<string>();
  const [noFusionAuthAccount, setNoFusionAuthAccount] = useState<boolean>(false);

  const getOverage = useCallback(async () => {
    const config = {
      params: {
        userId,
      },
    };
    const overageResponse = (await ScammerOverrideService.findByFilter(config)).data;

    if (overageResponse.data.length) {
      setOverride(overageResponse.data[0]);
    }
  }, [userId]);

  const getButtonText = () => (override.overridden ? 'Disable' : 'Enable');
  const getCurrentStatus = () => (override.overridden ? 'Enabled' : 'Disabled');

  const toggleOverage = async () => {
    const params = {
      id: override.id ?? undefined,
      createdBy: override.createdBy ?? { id: authenticationState.authentication.user.id },
      modifiedBy: { id: authenticationState.authentication.user.id },
      overriddenBy: override.overridden ? null : { id: authenticationState.authentication.user.id },
      user: { id: userId },
    };

    try {
      if (params.id) {
        await ScammerOverrideService.post(params);
      } else {
        await ScammerOverrideService.create(params);
      }
      getOverage();
    } catch (error) {
      const displayedError = DisplayService.getErrorResponse(
        error,
        'There was an error updating the scammer override.',
      );

      toast.error(displayedError.message);
    }
  };

  useEffect(() => {
    getOverage();
  }, [getOverage]);

  const loadAccountAccessStatus = useCallback(async () => {
    try {
      const { data } = await AdminFusionAuthAccountAccessService.getStatus(userId);

      setAccountLocked(data.locked);
      setAccountChangedAt(data.changedAt);
      setAccountChangedBy(data.changedBy);
      setNoFusionAuthAccount(false);
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.response?.status === 400) {
        setNoFusionAuthAccount(true);
        setAccountLocked(null);
      } else {
        toast.error('Error loading account access status.', { duration: 3000 });
      }
    }
  }, [userId]);

  useEffect(() => {
    loadAccountAccessStatus();
  }, [loadAccountAccessStatus]);

  const toggleAccountAccess = async () => {
    try {
      if (accountLocked) {
        await AdminFusionAuthAccountAccessService.unlock(userId);
        toast.success('User account access has been restored.');
      } else {
        await AdminFusionAuthAccountAccessService.lock(userId);
        toast.success('User account access has been locked.');
      }
      await loadAccountAccessStatus();
    } catch (error) {
      const displayedError = DisplayService.getErrorResponse(
        error,
        'There was an error updating account access.',
      );

      toast.error(displayedError.message);
    }
  };

  return (
    <Card className={classes.sectionCard}>
      <CardContent className={classes.sectionContent}>
        <Typography className={classes.sectionHeader} component='div'>
          Scammer Override
          <Chip
            label={getCurrentStatus()}
            size='small'
            color={override.overridden ? 'success' : 'error'}
            sx={{ ml: 1 }}
          />
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Enabling this will allow this user to create unlimited
          Bookings/Jobs/Messages within a 24 hour period.
        </Typography>
        <Button
          id='toggle-override'
          variant='outlined'
          size='small'
          onClick={toggleOverage}
          className={classes.actionButton}
        >
          {getButtonText()}
        </Button>

        <Typography className={classes.sectionHeader} component='div' sx={{ mt: 3 }}>
          Account Access Lock
          {!noFusionAuthAccount && (
            <Chip
              label={accountLocked ? 'Locked' : 'Unlocked'}
              size='small'
              color={accountLocked ? 'error' : 'success'}
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        {noFusionAuthAccount ? (
          <Typography variant='body2' color='text.secondary'>
            This user does not have a FusionAuth account.
          </Typography>
        ) : (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Locking this will block this user from accessing their account.
            </Typography>
            <Button
              id='toggle-account-access'
              variant='outlined'
              size='small'
              onClick={toggleAccountAccess}
              disabled={accountLocked === null}
              className={classes.actionButton}
            >
              {accountLocked ? 'Unlock' : 'Lock'}
            </Button>
            {accountChangedAt && accountChangedBy && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                {accountLocked ? 'Locked' : 'Unlocked'} {DateUtil.getReadableDateTime(accountChangedAt)}
                {' by '}{accountChangedBy}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default withRouter(ScammerOverride);
